/** Minimal change shape used to resolve a requester email (intake, RT, or reply recipients). */
export type RequesterSource = {
    intake?: { sender?: { email?: string } } | null;
    technicalContext?: { rt?: { requestorEmails?: string[] } } | null;
    replyRecipients?: {
        to?: { emails?: string[] } | null;
        cc?: { emails?: string[] } | null;
    } | null;
    completionNotify?: { emails?: string[] } | null;
    client?: { email?: string } | null;
};

function pushEmail(emails: string[], value?: string) {
    const email = String(value || '').trim();
    if (email && !emails.some((item) => item.toLowerCase() === email.toLowerCase()))
        emails.push(email);
}

/** Unique requester emails from intake, RT import, and default reply recipients. */
export function requesterEmailsOf(item: RequesterSource): string[] {
    const emails: string[] = [];
    pushEmail(emails, item.intake?.sender?.email);
    (item.technicalContext?.rt?.requestorEmails || []).forEach((email) => pushEmail(emails, email));
    (item.replyRecipients?.to?.emails || []).forEach((email) => pushEmail(emails, email));
    return emails;
}

/** Unique Cc emails from stored reply recipients. */
export function ccEmailsOf(item: RequesterSource): string[] {
    const emails: string[] = [];
    (item.replyRecipients?.cc?.emails || []).forEach((email) => pushEmail(emails, email));
    return emails;
}

/** First requester email, or a comma-separated list for display. */
export function requesterOf(item: RequesterSource): string {
    return requesterEmailsOf(item).join(', ');
}

/**
 * CRM client email used as reply To fallback when no real demandeur is known.
 * Returns null when demandeurs already exist (do not mix the two in the header).
 */
export function clientFallbackEmailOf(item: RequesterSource): string | null {
    if (requesterEmailsOf(item).length > 0)
        return null;
    const email = String(item.client?.email || '').trim();
    return email || null;
}

/**
 * Seed for replyRecipients.to when empty: real demandeur only (intake / RT).
 * Do NOT seed client.email — it is a send-time fallback shown in the header
 * as "(via client)" and must not appear as a removable À chip in the editor.
 */
export function defaultReplyToEmail(item: RequesterSource): string | null {
    return requesterEmailsOf({
        intake: item.intake,
        technicalContext: item.technicalContext,
        // Ignore stored reply recipients: this helper seeds them when empty.
    })[0] || null;
}

/**
 * Offer to keep the client address as a To recipient once another address
 * has been added. Until then the client email is the implicit requester.
 */
export function shouldOfferKeepClientRequester(
    implicitClientEmail: string | null | undefined,
    toEmails: string[] | null | undefined,
): boolean {
    const implicit = String(implicitClientEmail || '').trim().toLowerCase();
    if (!implicit)
        return false;
    const to = (toEmails || [])
        .map((email) => String(email || '').trim().toLowerCase())
        .filter(Boolean);
    if (to.length === 0)
        return false;
    return !to.includes(implicit);
}

export function matchesRequester(item: RequesterSource, query: string): boolean {
    const needle = query.trim().toLowerCase();
    if (!needle)
        return true;
    return requesterEmailsOf(item).some((email) => email.toLowerCase().includes(needle));
}
